import React, { useContext, useMemo } from 'react';

import { GasMonitorContext } from '../../../context';
import { Spin } from 'antd';
import circleImg from '../../../../../imgs/usage_spin_circle.png';

import { EchartsWithTheme, Empty, useTheme } from '@maxtropy/components';
import { formatThousandAndUnit, formatThousandUnit, keepOneOrLine, keepOnePercentOrLine } from '@/utils/utils';
import styles from './index.module.scss';
import { isEmpty } from 'lodash-es';

const PIE_COLORS = [
  '#52E7FF',
  '#FF477B',
  '#FFCB47',
  '#6F47FF',
  '#16DD8E',
  '#2D8DFF',
  '#52C41A',
  '#234ED9',
  '#FF9247',
  '#854ECA',
  '#4A90E2',
];

const GasCostPie: React.FC = () => {
  const { energyCostData: data, energyCostLoading: loading } = useContext(GasMonitorContext);
  const theme = useTheme();
  const costList = useMemo(() => {
    return data?.gasCostTrendPieCharts ?? [];
  }, [data]);

  const chartOption = useMemo(() => {
    const chartData = costList.map(item => ({
      name: item.mediumName ?? '--',
      value: item.cost ?? 0,
      proportion: item.proportion ?? 0,
    }));
    return {
      backgroundColor: 'transparent',
      color: PIE_COLORS,
      tooltip: {
        trigger: 'item',
        confine: true,
        formatter: (param: any) => {
          const moneyUnit = formatThousandAndUnit(param.data?.value);
          const percent = param.data?.proportion ?? 0;
          return `
               <div style="width:max-content;">
                 <div style="display:flex;gap: 12px;align-items:center;">
                 <span style="display:inline-block;margin-right: -10px">${param.marker}</span><span>${
            param.name
          }</span><span>${keepOneOrLine(percent * 100)}%</span><span>${moneyUnit.value}${moneyUnit.unit}元</span></div>
               </div>
             `;
        },
      },
      legend: {
        show: false,
      },
      graphic: {
        elements: [
          {
            type: 'image',
            left: 'center',
            top: 'center',
            style: {
              image: circleImg,
              x: -28,
              y: -28,
              width: 56,
              height: 56,
            },
            keyframeAnimation: [
              {
                // 动画时长，单位 ms
                duration: 80000,
                // 设置旋转动画是否循环
                loop: true,
                easing: 'linear',
                // 动画的关键帧
                keyframes: [
                  {
                    // percent为关键帧的位置,0为第一个,1为最后一个
                    percent: 0,
                    rotation: 0,
                    transition: ['rotation'],
                  },
                  {
                    percent: 1,
                    rotation: 360,
                    transition: ['rotation'],
                  },
                ],
              },
            ],
          },
        ],
      },
      series: [
        {
          type: 'pie',
          radius: ['35%', '95%'],
          center: ['50%', '50%'],
          padAngle: 4,
          itemStyle: {
            borderRadius: 8,
          },
          avoidLabelOverlap: false,
          showEmptyCircle: false,
          minAngle: 4,
          label: {
            show: false,
          },
          data: chartData,
        },
      ],
    };
  }, [costList]);

  return (
    <div className={styles.pieContainer}>
      <Spin spinning={loading}>
        {isEmpty(costList) ? (
          <Empty style={{ margin: '20px auto 0' }} />
        ) : (
          <>
            <div className={styles.chartWrapper}>
              <EchartsWithTheme
                empty={{
                  isShowEmpty: !costList || costList.length === 0,
                  emptyNode: <Empty />,
                }}
                loading={loading}
                theme={theme}
                style={{ height: '100%', flex: 1, minHeight: 180 }}
                option={chartOption}
              />
            </div>
            <div className={styles.legendWrapper}>
              <div className={styles.totalCost}>
                <span className={styles.totalLabel}>总成本:</span>
                <span className={styles.totalValue}>{formatThousandUnit(data?.totalCost)}元</span>
              </div>
              <div className={styles.legendList}>
                {costList.map((item, index) => {
                  const color = PIE_COLORS[index % PIE_COLORS.length];
                  return (
                    <div key={item.mediumId ?? index} className={styles.legendItem}>
                      <span className={styles.legendDot} style={{ background: color }} />
                      <span className={styles.legendName}>{item.mediumName ?? '--'}</span>
                      <span className={styles.legendPercent}>{keepOnePercentOrLine(item.proportion)}%</span>
                      <span className={styles.legendValue}>{formatThousandUnit(item.cost)}元</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </Spin>
    </div>
  );
};

export default GasCostPie;
